import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { SimpleDocCommand } from "./types";
import { createFileLogger } from '../../../../utils/logger';
import { createDocumentReader } from "../../services/doc/DocumentReader";
import { API_BASE_URL } from '../../../../api/config';
import { documentHighlightService } from '../../../../services/documentHighlight';

const log = createFileLogger('DETECT_AI');

export const detectAICommand: SimpleDocCommand = {
  id: 'detect_ai',
  
  description: `Detect AI-generated content.

**SUPPORTED ENTITIES:**
Docs (doc_*)

Max 2,000 words per call (auto-truncated if larger). For large documents, use indexRange to analyze sections.

**SELECTORS:**

"all" - Entire document (up to 2K words)
{"indexRange": {"start": 0, "count": 100}} - Specific node range

**RETURNS:**

- classification: "HUMAN_ONLY", "AI_ONLY", or "MIXED"
- aiProbability: 0-100%
- humanProbability: 0-100%
- confidence: "low", "medium", or "high"

**EXAMPLE:**

{ "id": "doc_meadow_silver_compass", "selector": "all" }`,

  logicalName: 'detect_ai',
  version: 'v1',
  appType: 'docs',
  
  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.data) {
        const data = result.data;
        return {
          type: 'detect_ai_result',
          summary: result.message || `AI Detection: ${data.classification}`,
          data: {
            classification: data.classification,
            aiProbability: data.aiProbability,
            humanProbability: data.humanProbability,
            confidence: data.confidence,
            resultMessage: data.resultMessage,
            documentId: toolInput?.id || 'unknown',
            wordCount: data.wordCount
          },
          meta: {
            duration: result.metadata?.executionTime,
            provider: 'API'
          }
        };
      }
      
      return {
        type: 'tool_summary',
        summary: 'AI detection complete',
        data: result?.data || {}
      };
      
    } catch (error) {
      console.warn('[DETECT_AI] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'AI detection complete',
        data: {}
      };
    }
  },
  
  formatResultPreview: (result: any, toolInput: any) => {
    try {
      if (result?.data) {
        const { classification, aiProbability, confidence } = result.data;
        return `AI Detection: ${classification} (${Math.round(aiProbability)}% AI, ${confidence} confidence)`;
      }
      return 'AI detection complete';
    } catch (error) {
      console.warn('[DETECT_AI_FORMATTER] Error:', error);
      return 'AI detection complete';
    }
  },
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Document ID to analyze (e.g. "doc_meadow_silver_compass")',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Document ID cannot be empty';
          if (value.startsWith('note_') || value.startsWith('sheet_') || value.startsWith('canvas_') || value.startsWith('slide_') || value.startsWith('presentation_')) {
            return 'detect_ai is for Documents only. Cannot detect AI in Notes/Sheets/Canvas/Slides.';
          }
          if (value.startsWith('pdf_') || value.startsWith('file_')) {
            return 'detect_ai is for Documents only. Cannot detect AI in PDFs.';
          }
          return null;
        },
        examples: [
          'doc_meadow_silver_compass',
          'doc_cascade_thunder_phoenix',
          'doc_driftwood_village_butterfly'
        ]
      },
      {
        name: 'selector',
        type: 'string',
        description: 'What to analyze: "all" (entire document), or JSON selector like {"indexRange": {"start": 0, "count": 100}}. Default: "all"',
        required: false,
        defaultValue: 'all',
        examples: [
          'all',
          '{"indexRange": {"start": 0, "count": 100}}',
          '{"indexRange": {"start": 50, "count": 200}}'
        ]
      }
    ]
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = performance.now();
    
    try {
      log.log('[DETECT_AI] Starting AI detection', { params });
      
      const { id: rawDocId, selector = 'all' } = params;
      
      if (!rawDocId) {
        return createErrorResult(
          'Document ID is required',
          'id parameter is required',
          'validation'
        );
      }
      
      // Progress update
      context.onProgress?.({
        type: 'streaming_update',
        toolUseId: context.executionId,
        message: 'Reading document...'
      });
      
      // Use DocumentReader to get text content
      const { reader, error: readerError } = await createDocumentReader(rawDocId, context);
      if (readerError || !reader) {
        log.error('[DETECT_AI] Failed to create document reader:', {
          id: rawDocId,
          error: readerError || 'Reader creation failed'
        });
        return createErrorResult(
          'Document not accessible',
          readerError || `Could not access document (llmId: ${rawDocId})`,
          'system'
        );
      }
      
      // Parse selector to get specific content
      let textToAnalyze = '';
      let nodeIds: string[] = [];
      let parsedSelector: any = selector;
      
      // Handle JSON selectors (like doc_read_v2 does)
      if (typeof selector === 'string' && selector !== 'all' && selector.startsWith('{')) {
        try {
          parsedSelector = JSON.parse(selector);
        } catch (e) {
          log.warn('[DETECT_AI] Failed to parse selector JSON:', selector);
        }
      }
      
      // Handle indexRange selector
      if (typeof parsedSelector === 'object' && parsedSelector.indexRange) {
        const allContent = reader.readAll();
        const allNodes = allContent.nodes || [];
        
        const { start, count } = parsedSelector.indexRange;
        const fromIndex = start || 0;
        const toIndex = Math.min(fromIndex + (count || 100), allNodes.length);
        
        const selectedNodes = allNodes.slice(fromIndex, toIndex);
        textToAnalyze = selectedNodes.map((n: any) => n.text || '').join('\n\n');
        nodeIds = selectedNodes.map((n: any) => n.id);
        
      } else {
        // Default: read all
        const allContent = reader.readAll();
        const allNodes = allContent.nodes || [];
        textToAnalyze = allNodes.map((n: any) => n.text || '').join('\n\n');
        nodeIds = allNodes.map((n: any) => n.id);
      }
      
      if (!textToAnalyze || textToAnalyze.trim().length === 0) {
        return createErrorResult(
          'No content to analyze',
          'Selected content is empty. Please select a document or section with text content.',
          'validation'
        );
      }
      
      // Count words
      let wordCount = textToAnalyze.trim().split(/\s+/).filter(w => w.trim()).length;
      const originalWordCount = wordCount;
      
      // Validate minimum word count
      if (wordCount < 10) {
        return createErrorResult(
          'Content too short for analysis',
          `AI detection requires at least 10 words (found ${wordCount}). Current content is too short to analyze reliably. Please select a larger section or use a document with more content.`,
          'validation'
        );
      }
      
      // Enforce 2K word maximum for API (truncate if needed)
      const MAX_WORDS = 2000;
      if (wordCount > MAX_WORDS) {
        const words = textToAnalyze.trim().split(/\s+/);
        const truncatedWords = words.slice(0, MAX_WORDS);
        textToAnalyze = truncatedWords.join(' ');
        wordCount = MAX_WORDS;
        
        log.log('[DETECT_AI] Truncated content to API limit', {
          originalWords: originalWordCount,
          truncatedWords: MAX_WORDS
        });
        
        context.onProgress?.({
          type: 'streaming_update',
          toolUseId: context.executionId,
          message: `Analyzing first ${MAX_WORDS.toLocaleString()} words of ${originalWordCount.toLocaleString()}-word selection...`
        });
      }
      
      log.log('[DETECT_AI] Content extracted', {
        wordCount,
        originalWordCount,
        nodeCount: nodeIds.length,
        textLength: textToAnalyze.length
      });
      
      const commandId = context.executionId || `detect-ai-${Date.now()}`;
      
      log.log('[DETECT_AI] Starting highlight', {
        commandId,
        hasExecutionId: !!context.executionId,
        nodeCount: nodeIds.length
      });
      
      documentHighlightService.highlight(commandId, nodeIds, {
        commandName: 'detect_ai',
        documentId: rawDocId,
        reason: 'AI content detection analysis'
      });
      
      try {
        // Progress update
        context.onProgress?.({
          type: 'streaming_update',
          toolUseId: context.executionId,
          message: `Analyzing ${wordCount} words...`
        });
        
        // Call backend detection API
        const authToken = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/ai-detection/detect-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          text: textToAnalyze,
          documentId: rawDocId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail || '';
        
        let userMessage = 'AI detection failed';
        let errorDetail = '';
        
        // Provide specific, helpful error messages based on status code
        if (response.status === 503 || response.status === 504) {
          userMessage = 'Service temporarily unavailable';
          errorDetail = 'AI detection API is currently unavailable. You can try:\n\n1. Use the ai_detect command instead (internal LLM-based detection)\n2. Wait a moment and try again\n\nCommand: ai_detect with same document ID';
        } else if (response.status === 429) {
          userMessage = 'Rate limit exceeded';
          errorDetail = 'API rate limit reached. Please wait a moment and try again, or use ai_detect command for unlimited internal detection.';
        } else if (response.status === 401 || response.status === 403) {
          userMessage = 'Authentication failed';
          errorDetail = 'API authentication error. Please contact support if this persists.';
        } else if (response.status === 400) {
          userMessage = 'Invalid request';
          errorDetail = detail || 'The content could not be analyzed. Try selecting a different section or check content formatting.';
        } else {
          errorDetail = detail || `API request failed with status ${response.status}. Try using ai_detect command as an alternative.`;
        }
        
        return createErrorResult(userMessage, errorDetail, 'execution');
      }
      
      const result = await response.json();
      
      log.log('[DETECT_AI] Analysis complete', {
        classification: result.classification,
        aiProbability: result.aiProbability,
        confidence: result.confidence
      });
      
      const executionTime = Math.round(performance.now() - startTime);
      
      return createSuccessResult(
        `AI Detection: ${result.classification} - ${Math.round(result.aiProbability)}% AI, ${Math.round(result.humanProbability)}% Human (${result.confidence} confidence)`,
        {
          classification: result.classification,
          aiProbability: result.aiProbability,
          humanProbability: result.humanProbability,
          confidence: result.confidence,
          confidenceScore: result.confidenceScore,
          resultMessage: result.resultMessage,
          documentId: rawDocId,
          wordCount,
          nodeIds,
          sentences: result.sentences,
          rawResponse: result.rawResponse
        },
        {
          metadata: {
            executionTime,
            affectedEntities: [rawDocId]
          }
        }
      );
      
      } finally {
        // GUARANTEED CLEANUP - Clear highlight whether success or error
        documentHighlightService.clear(commandId);
      }
      
    } catch (error: any) {
      log.error('[DETECT_AI] Detection failed:', error);
      
      // Provide helpful fallback message
      let errorMessage = error.message || 'Unknown error occurred during detection';
      let userTitle = 'AI detection failed';
      
      // Network errors
      if (error.message?.includes('fetch') || error.message?.includes('network') || error.name === 'TypeError') {
        userTitle = 'Network error';
        errorMessage = 'Could not connect to AI detection API. Check your internet connection or try using ai_detect command for offline detection.';
      }
      // Timeout errors
      else if (error.message?.includes('timeout') || error.name === 'AbortError') {
        userTitle = 'Request timeout';
        errorMessage = 'AI detection API took too long to respond. Try a smaller section or use ai_detect command instead.';
      }
      
      return createErrorResult(
        userTitle,
        errorMessage,
        'execution',
        {
          metadata: {
            executionTime: Math.round(performance.now() - startTime),
            followupSuggestions: ['Try using ai_detect command as an alternative']
          }
        }
      );
    }
  }
};

